import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { EyeOff } from 'lucide-react';
import { HostSettingsLayout } from '../../components/HostSettingsLayout';
import { HostSettingsCard, HostToggleRow } from '../../components/HostSettingsCard';
import { fetchAccountSettings, updateAccountPrivacy } from '../../utils/accountSettingsApi';
import { DeleteAccountSection } from '../../components/DeleteAccountSection';
import { DownloadMyDataSection } from '../../components/DownloadMyDataSection';
import CookiePreferencesCard from '../../components/CookiePreferencesCard';
import AllowPhoneCallsCard from '../../components/AllowPhoneCallsCard';
import AllowReadReceiptsCard from '../../components/AllowReadReceiptsCard';

// Shared by host, venue owner and venue staff/admin privacy routes.
// 2026-06: the HOST "Receive messages from guests" toggle was removed — hosts
// now ALWAYS receive guest messages. Only the VENUE scope (/venue/* routes)
// still exposes a "Receive messages from guests" toggle, and it AUTO-SAVES on
// change (no DONE button). Host/staff routes show account deletion only.
export default function HostPrivacyPage() {
  const { pathname } = useLocation();
  const isVenueScope = pathname.startsWith('/venue');

  const [receiveMessages, setReceiveMessages] = useState(true);

  useEffect(() => {
    if (!isVenueScope) return;
    fetchAccountSettings()
      .then((settings) => setReceiveMessages(settings.privacy?.receive_venue_messages !== false))
      .catch(() => toast.error('Failed to load privacy settings'));
  }, [isVenueScope]);

  const handleToggle = async () => {
    const next = !receiveMessages;
    setReceiveMessages(next); // optimistic
    try {
      await updateAccountPrivacy({ receive_venue_messages: next });
      // Let the owner-only "Guest messages off" pill refresh live if it's mounted.
      window.dispatchEvent(new Event('allure:venue-messages-pref-changed'));
      toast.success('Privacy updated');
    } catch (err) {
      setReceiveMessages(!next); // rollback
      toast.error(err.response?.data?.detail || 'Failed to update privacy settings');
    }
  };

  return (
    <HostSettingsLayout title="Privacy" dataTestId="host-privacy-page">
      {isVenueScope ? (
        <HostSettingsCard data-testid="host-privacy-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <EyeOff size={24} color="var(--text)" />
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: 0 }}>Privacy Controls</p>
          </div>

          <HostToggleRow
            label="Receive messages from guests"
            description="Let guests start a chat with your venue. Turn off to stop receiving new guest messages — your team chats with Allure and staff are never affected."
            checked={receiveMessages}
            onChange={handleToggle}
            dataTestId="host-privacy-receive-messages-toggle"
          />
        </HostSettingsCard>
      ) : (
        // Voice calling only exists between hosts and guests. The venue side
        // of the app never places or receives calls, so this card is
        // intentionally hidden when the page is mounted under /venue.
        <AllowPhoneCallsCard dataTestId="host-privacy-allow-phone-calls" />
      )}

      <AllowReadReceiptsCard dataTestId={isVenueScope ? 'venue-privacy-allow-read-receipts' : 'host-privacy-allow-read-receipts'} />

      <CookiePreferencesCard dataTestId="host-privacy-cookie-card" />

      <DownloadMyDataSection dataTestIdPrefix="host-privacy" />

      <DeleteAccountSection dataTestIdPrefix="host-privacy" />
    </HostSettingsLayout>
  );
}
